import {
  constructIntervalFromAPI,
  deserializeInterval,
  intervalsAreEqual,
  serializeInterval,
} from "./intervals";
import { safeName } from "./recordTree";
import {
  HGAnyRecord,
  HGAnyRecordAPI,
  HGAnyRecordSerialized,
  HGEra,
  HGRecord,
  HGTimeRecordSerialized,
} from "./types";

export function serializeRecord(
  record: HGAnyRecord | null,
): HGAnyRecordSerialized | null {
  if (!record) return null;

  if ("interval" in record) {
    return Object.assign(
      { ...record },
      {
        start_second: record.start_second.toString(),
        end_second: record.end_second && record.end_second.toString(),
        interval: serializeInterval(record.interval),
        end_interval:
          record.end_interval && serializeInterval(record.end_interval),
      },
    );
  } else {
    return { ...record };
  }
}
export function deserializeRecord(
  record: HGAnyRecordSerialized | null,
): HGAnyRecord | null {
  if (!record) return null;

  if ("interval" in record) {
    return Object.assign(
      { ...record },
      {
        start_second: BigInt(record.start_second),
        end_second: record.end_second && BigInt(record.end_second),
        interval: deserializeInterval(record.interval),
        end_interval:
          record.end_interval && deserializeInterval(record.end_interval),
      },
    );
  } else {
    return { ...record };
  }
}

/**
 * A record off a single-record endpoint, in the shape the client works in.
 *
 * The timeline gets this for free: `/records/by-ival` nests its records under
 * an era, and `expandRecordsMap` signs `n` and the second offsets as it walks
 * out of that nesting. A record fetched on its own -- the record page, the
 * edit form, the `title_record` embedded in a collection -- arrives with the
 * era sitting beside an unsigned magnitude instead, and nothing had been
 * applying it. Positive `n` is AD, so a BC record came out mirrored across the
 * epoch: the same distance from year 1, counted the other way.
 *
 * Applied in `transformResponse`, so everything downstream keeps reading
 * `deserializeRecord` and never sees the wire shape.
 */
export function apiRecordToSerialized(
  record: HGAnyRecordAPI,
): HGAnyRecordSerialized {
  const base = {
    ...record,
    // Both arrive as ISO strings while the rest of the app reads epoch millis
    // -- the same normalisation expandRecordsMap does for the timeline.
    created: new Date(record.created).getTime(),
    modified:
      record.modified == null ? null : new Date(record.modified).getTime(),
  };

  if (!("interval" in base) || !base.interval) {
    return base as HGRecord;
  }

  const { interval, end_interval } = base;
  const signSecond = (magnitude: string | number | null, era: HGEra) =>
    magnitude == null ? null : (era == HGEra.BC ? "-" : "") + magnitude;

  return {
    ...base,
    interval: serializeInterval(
      constructIntervalFromAPI(interval.n, interval.it, interval.era),
    ),
    end_interval: end_interval
      ? serializeInterval(
          constructIntervalFromAPI(
            end_interval.n,
            end_interval.it,
            end_interval.era,
          ),
        )
      : null,
    start_second: signSecond(base.start_second, interval.era),
    // An unfinished record has no end interval and so no end second, which is
    // how the timeline represents one too.
    end_second: end_interval
      ? signSecond(base.end_second, end_interval.era)
      : null,
    // The name/intersection fields HGTimeRecordSerialized carries are the
    // listing's work; a record fetched on its own picks them up from
    // intervalNames() at the point of display.
  } as HGTimeRecordSerialized;
}

/**
 * The `intervalName` / `endIntervalName` pair that record views render.
 *
 * `enrichRecords` sets these on everything coming out of `/records/by-ival`, but
 * a record fetched on its own — the record page, or the title record embedded in
 * a collection — arrives without them. Same rule in both places: the end name is
 * only set when the end interval is genuinely a different interval, so a record
 * that starts and ends in 1750 reads "1750" and not "1750 - 1750".
 */
export function intervalNames(record: HGAnyRecord): {
  intervalName: string;
  endIntervalName: string | null;
} {
  if (!("interval" in record) || !record.interval) {
    return { intervalName: "", endIntervalName: null };
  }
  const endInterval = record.end_interval;
  return {
    intervalName: safeName(record.interval),
    endIntervalName:
      endInterval && !intervalsAreEqual(record.interval, endInterval)
        ? safeName(endInterval)
        : null,
  };
}

/**
 * Case-insensitive substring match over a record's user-visible text: title,
 * content, and any link labels/URLs. `query` must already be trimmed and
 * lowercased by the caller — matching against a whole result set is hot
 * enough that it isn't worth redoing that per record.
 */
export function recordMatches(record: HGRecord, query: string): boolean {
  if (record.title.toLowerCase().includes(query)) return true;
  if (record.content.toLowerCase().includes(query)) return true;
  return (record.url ?? []).some(
    (u) =>
      u.label.toLowerCase().includes(query) ||
      u.url.toLowerCase().includes(query),
  );
}
